import { useEffect, useState } from 'react';

import { useTaxonomyContext } from '../context/TaxonomyContext';

import { CustomerSelect } from '@/components/CustomerSelect';
import { Card, CardContent } from '@/components/ui/Card';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/Select';
import { useGetCustomerById } from '@/hooks/useGetCustomerById';

interface TaxonomySelectProps {
  customerId: number;
  setCustomerId: (customerId: number) => void;
  selectedVersion: string;
  setSelectedVersion: (selectedVersion: string) => void;
  versions: string[];
}

export function TaxonomySelects({
  customerId,
  setCustomerId,
  selectedVersion,
  setSelectedVersion,
  versions,
}: TaxonomySelectProps) {
  const { setTaxonomiesUpdated } = useTaxonomyContext();

  const { customer } = useGetCustomerById(customerId);

  const [language, setLanguage] = useState([customer?.language || 'PT-BR']);

  const [industry, setIndustry] = useState('moda');

  function handleLanguageChange(value: string) {
    setLanguage([value]);
  }

  function handleCustomerChange(customerId: string) {
    setCustomerId(Number(customerId));
    setSelectedVersion('');
    setLanguage([customer?.language || 'PT-BR']);
    setTaxonomiesUpdated({
      data: [],
      taxonomiesToRemove: [],
      version: '',
    });
  }

  function handleVersionChange(value: string) {
    setSelectedVersion(value);
    setTaxonomiesUpdated({
      data: [],
      taxonomiesToRemove: [],
      version: value,
    });
  }

  useEffect(() => {
    if (!versions) return;
    if (versions.length === 0 && selectedVersion !== '') {
      setSelectedVersion('');
    } else if (versions.length > 0 && selectedVersion === '') {
      setSelectedVersion(versions[0]);
      setTaxonomiesUpdated({
        data: [],
        taxonomiesToRemove: [],
        version: versions[0],
      });
    }
  }, [versions, selectedVersion, setSelectedVersion, setTaxonomiesUpdated]);

  return (
    <div>
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <Label htmlFor="client">Customer</Label>

              <CustomerSelect
                value={customerId.toString()}
                onValueChange={handleCustomerChange}
                additionalOptions={[{ value: '0', label: 'Default' }]}
              />
            </div>

            <div>
              <Label htmlFor="language">Language</Label>
              <Select value={language[0]} onValueChange={handleLanguageChange}>
                <SelectTrigger id="language" className="w-full">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>

                <SelectContent>
                  {language?.map((language: string) => (
                    <SelectItem key={language} value={language}>
                      {language}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="industry">Industry</Label>
              <Select value={industry} onValueChange={setIndustry} disabled>
                <SelectTrigger id="industry" className="w-full">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="moda">Moda</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="version">Version</Label>
              <Select
                value={selectedVersion}
                onValueChange={handleVersionChange}
                disabled={!versions?.length}
              >
                <SelectTrigger id="version" className="w-full">
                  <SelectValue placeholder="Select version" />
                </SelectTrigger>

                <SelectContent>
                  {versions?.map((version: string) => (
                    <SelectItem key={version} value={version}>
                      {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
